"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ElementType,
  type MouseEvent,
} from "react";
import { ArrowRight, Link, Zap } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { AppCopy, Direction } from "@/lib/i18n";

interface TimelineItem {
  id: number;
  title: string;
  date: string;
  content: string;
  category: string;
  icon: ElementType;
  relatedIds: readonly number[];
  status: "completed" | "in-progress" | "pending";
  energy: number;
}

interface RadialOrbitalTimelineProps {
  copy: AppCopy["timeline"];
  direction: Direction;
  timelineData: readonly TimelineItem[];
}

export default function RadialOrbitalTimeline({
  copy,
  direction,
  timelineData,
}: RadialOrbitalTimelineProps) {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    {},
  );
  const [viewMode] = useState<"orbital">("orbital");
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [pulseEffect, setPulseEffect] = useState<Record<number, boolean>>({});
  const [centerOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1024 : window.innerWidth,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const rotationAngleRef = useRef(0);
  const isCompact = viewportWidth < 640;
  const orbitRadius = isCompact
    ? Math.max(96, Math.min(138, viewportWidth / 2 - 66))
    : 200;
  const orbitSize = orbitRadius * 2;

  const handleContainerClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const getRelatedItems = (itemId: number): readonly number[] => {
    const currentItem = timelineData.find((item) => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  };

  const centerViewOnNode = (nodeId: number) => {
    if (viewMode !== "orbital" || !nodeRefs.current[nodeId]) return;

    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const totalNodes = timelineData.length;
    const targetAngle = (nodeIndex / totalNodes) * 360;

    const centeredAngle = 270 - targetAngle;
    rotationAngleRef.current = centeredAngle;
    setRotationAngle(centeredAngle);
  };

  const toggleItem = (id: number) => {
    setExpandedItems((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (Number.parseInt(key) !== id) {
          newState[Number.parseInt(key)] = false;
        }
      });

      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);

        const relatedItems = getRelatedItems(id);
        const newPulseEffect: Record<number, boolean> = {};
        relatedItems.forEach((relId) => {
          newPulseEffect[relId] = true;
        });
        setPulseEffect(newPulseEffect);

        centerViewOnNode(id);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }

      return newState;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setRotationAngle(rotationAngleRef.current);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const calculateNodePosition = useCallback(
    (index: number, total: number, angleOverride = rotationAngle) => {
      const angle = ((index / total) * 360 + angleOverride) % 360;
      const radian = (angle * Math.PI) / 180;

      const x = orbitRadius * Math.cos(radian) + centerOffset.x;
      const y = orbitRadius * Math.sin(radian) + centerOffset.y;

      const zIndex = 100 + index;
      const opacity = 1;

      return { x, y, zIndex, opacity };
    },
    [centerOffset.x, centerOffset.y, orbitRadius, rotationAngle],
  );

  const applyNodePositions = useCallback(
    (angle: number) => {
      rotationAngleRef.current = angle;

      timelineData.forEach((item, index) => {
        const node = nodeRefs.current[item.id];
        if (!node) return;

        const position = calculateNodePosition(index, timelineData.length, angle);
        node.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
        node.style.opacity = String(position.opacity);
        node.style.zIndex = String(position.zIndex);
      });
    },
    [calculateNodePosition, timelineData],
  );

  useLayoutEffect(() => {
    applyNodePositions(rotationAngleRef.current);
  }, [applyNodePositions]);

  useEffect(() => {
    let rotationFrame = 0;
    let lastFrameTime: number | null = null;
    const degreesPerMs = 0.45 / 80;

    const rotate = (time: number) => {
      if (lastFrameTime === null) {
        lastFrameTime = time;
      }

      const delta = Math.min(time - lastFrameTime, 48);
      const newAngle = (rotationAngleRef.current + delta * degreesPerMs) % 360;
      applyNodePositions(newAngle);
      lastFrameTime = time;

      rotationFrame = window.requestAnimationFrame(rotate);
    };

    if (autoRotate && viewMode === "orbital") {
      rotationFrame = window.requestAnimationFrame(rotate);
    }

    return () => {
      if (rotationFrame) {
        window.cancelAnimationFrame(rotationFrame);
      }
    };
  }, [applyNodePositions, autoRotate, viewMode]);

  const isRelatedToActive = (itemId: number): boolean => {
    if (!activeNodeId) return false;
    const relatedItems = getRelatedItems(activeNodeId);
    return relatedItems.includes(itemId);
  };

  const getStatusStyles = (status: TimelineItem["status"]): string => {
    switch (status) {
      case "completed":
        return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200";
      case "in-progress":
        return "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-200";
      case "pending":
        return "border-slate-300 bg-slate-100 text-slate-600 dark:border-white/20 dark:bg-white/10 dark:text-slate-300";
      default:
        return "border-slate-300 bg-slate-100 text-slate-600 dark:border-white/20 dark:bg-white/10 dark:text-slate-300";
    }
  };

  return (
    <div
      id="cases"
      className="relative flex h-[100svh] min-h-[620px] w-full scroll-mt-14 flex-col items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_center,rgba(239,246,255,0.95)_0%,rgba(255,255,255,1)_48%,rgba(241,245,249,1)_100%)] transition-colors sm:h-screen sm:min-h-[720px] dark:bg-[radial-gradient(circle_at_center,rgba(15,23,42,0.9)_0%,rgba(0,0,0,1)_58%,rgba(0,0,0,1)_100%)]"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <header className="pointer-events-none absolute inset-x-0 top-8 z-20 flex flex-col items-center gap-2 px-4 text-center sm:top-16 sm:gap-3">
        <h2 className="text-3xl font-semibold leading-tight text-slate-900 drop-shadow-[0_14px_34px_rgba(148,163,184,0.24)] sm:text-5xl lg:text-6xl dark:text-white">
          {copy.heading}
        </h2>
        {copy.subheading && (
          <p className="max-w-xs text-xs font-medium text-slate-600 sm:max-w-xl sm:text-base dark:text-slate-300">
            {copy.subheading}
          </p>
        )}
      </header>
      <div className="relative flex h-full w-full max-w-4xl items-center justify-center px-3 pt-20 sm:px-0 sm:pt-0">
        <div
          className="absolute flex h-full w-full items-center justify-center"
          ref={orbitRef}
          style={{
            perspective: "1000px",
            transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,
          }}
        >
          <div className="absolute z-10 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-teal-500 shadow-lg shadow-blue-500/15 sm:h-16 sm:w-16 sm:animate-pulse">
            <div className="absolute hidden h-20 w-20 animate-ping rounded-full border border-blue-500/25 opacity-70 sm:block dark:border-white/20"></div>
            <div
              className="absolute hidden h-24 w-24 animate-ping rounded-full border border-blue-500/15 opacity-50 sm:block dark:border-white/10"
              style={{ animationDelay: "0.5s" }}
            ></div>
            <div className="h-7 w-7 rounded-full bg-white/80 backdrop-blur-md sm:h-8 sm:w-8"></div>
          </div>

          <div
            className="absolute rounded-full border border-slate-300/70 dark:border-white/10"
            style={{ height: orbitSize, width: orbitSize }}
          ></div>

          {timelineData.map((item, index) => {
            const position = calculateNodePosition(
              index,
              timelineData.length,
              rotationAngleRef.current,
            );
            const isExpanded = expandedItems[item.id];
            const isRelated = isRelatedToActive(item.id);
            const isPulsing = pulseEffect[item.id];
            const Icon = item.icon;
            const auraSize = isCompact ? item.energy * 0.28 + 34 : item.energy * 0.5 + 40;

            const nodeStyle = {
              transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
              zIndex: isExpanded ? 200 : position.zIndex,
              opacity: isExpanded ? 1 : position.opacity,
              willChange: "transform",
              backfaceVisibility: "hidden",
            };

            return (
              <div
                key={item.id}
                ref={(el) => {
                  nodeRefs.current[item.id] = el;
                }}
                className={`absolute cursor-pointer ${
                  autoRotate
                    ? "transform-gpu"
                    : "transition-transform duration-500 ease-out"
                }`}
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >
                <div
                  className={`absolute -inset-1 rounded-full bg-[radial-gradient(circle,rgba(37,99,235,0.18)_0%,rgba(37,99,235,0)_70%)] dark:bg-[radial-gradient(circle,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_70%)] ${
                    isPulsing ? "animate-pulse duration-1000" : ""
                  }`}
                  style={{
                    width: `${auraSize}px`,
                    height: `${auraSize}px`,
                    left: `-${(auraSize - 40) / 2}px`,
                    top: `-${(auraSize - 40) / 2}px`,
                  }}
                ></div>

                <div
                  className={`
                    flex h-9 w-9 items-center justify-center rounded-full sm:h-10 sm:w-10
                    ${
                      isExpanded
                        ? "bg-slate-950 text-white dark:bg-white dark:text-black"
                        : isRelated
                          ? "bg-blue-500/20 text-blue-950 dark:bg-white/50 dark:text-black"
                          : "bg-white text-slate-700 dark:bg-black dark:text-white"
                    }
                    border-2
                    ${
                      isExpanded
                        ? "border-slate-950 shadow-lg shadow-slate-900/20 dark:border-white dark:shadow-white/30"
                        : isRelated
                          ? "animate-pulse border-blue-500 dark:border-white"
                          : "border-slate-300 dark:border-white/40"
                    }
                    transform transition-transform duration-300
                    ${isExpanded ? "scale-150" : ""}
                  `}
                >
                  <Icon size={isCompact ? 15 : 16} />
                </div>

                <div
                  className={`
                    absolute top-11 left-1/2 w-28 -translate-x-1/2 text-center text-[11px] font-semibold leading-tight tracking-normal sm:top-12 sm:w-max sm:max-w-none sm:whitespace-nowrap sm:text-xs sm:tracking-wider
                    transition-all duration-300
                    ${
                      isExpanded
                        ? "scale-125 text-slate-950 dark:text-white"
                        : "text-slate-500 dark:text-white/70"
                    }
                  `}
                >
                  {item.title}
                </div>

                {isExpanded && (
                  <Card
                    className="absolute top-20 left-1/2 w-72 -translate-x-1/2 overflow-visible border-slate-200/80 bg-white/95 text-start shadow-xl shadow-slate-900/10 backdrop-blur-lg dark:border-white/30 dark:bg-black/90 dark:shadow-white/10 sm:w-80"
                    dir={direction}
                  >
                    <div className="absolute -top-3 left-1/2 h-3 w-px -translate-x-1/2 bg-slate-300 dark:bg-white/50"></div>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <Badge
                          className={`px-2 text-xs ${getStatusStyles(
                            item.status,
                          )}`}
                        >
                          {item.status === "completed"
                            ? copy.status.completed
                            : item.status === "in-progress"
                              ? copy.status.inProgress
                              : copy.status.pending}
                        </Badge>
                        <span className="font-mono text-xs text-muted-foreground">
                          {item.date}
                        </span>
                      </div>
                      <CardTitle className="mt-2 text-sm">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm leading-6 text-slate-600 dark:text-white/80">
                      <p>{item.content}</p>

                      <div className="mt-4 border-t border-slate-200 pt-3 dark:border-white/10">
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="flex items-center">
                            <Zap size={10} className="me-1" />
                            {copy.energyLevel}
                          </span>
                          <span className="font-mono">{item.energy}%</span>
                        </div>
                        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                            style={{ width: `${item.energy}%` }}
                          ></div>
                        </div>
                      </div>

                      {item.relatedIds.length > 0 && (
                        <div className="mt-4 border-t border-slate-200 pt-3 dark:border-white/10">
                          <div className="mb-2 flex items-center">
                            <Link
                              size={10}
                              className="me-1 text-slate-500 dark:text-white/70"
                            />
                            <h4 className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-white/70">
                              {copy.connectedNodes}
                            </h4>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {item.relatedIds.map((relatedId) => {
                              const relatedItem = timelineData.find(
                                (i) => i.id === relatedId,
                              );
                              return (
                                <Button
                                  key={relatedId}
                                  variant="outline"
                                  size="sm"
                                  className="flex h-6 items-center rounded-none border-slate-200 bg-transparent px-2 py-0 text-xs text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-950 dark:border-white/20 dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItem(relatedId);
                                  }}
                                >
                                  {relatedItem?.title}
                                  <ArrowRight
                                    size={8}
                                    className={`text-slate-400 dark:text-white/60 ${
                                      direction === "rtl"
                                        ? "me-1 rotate-180"
                                        : "ms-1"
                                    }`}
                                  />
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
